/** The work attached to one record — tasks, notes, flags and the approval in flight.
 *
 * **Shared across every kind, which is EXP-15's remaining half** (ADR-0037). This was inside
 * `Record.tsx` and took an `Activity`, so a risk, a source or an initiative had no page where its
 * work lived — even though `/records/{id}/work` has answered for every kind over HTTP since S05.
 * The three things it actually needed from an activity record were an id, a kind and a revision
 * number, and all three exist for every kind, so the whole tab moved here unchanged in behaviour.
 *
 * **The route set is per kind and was hard-coded.** `useApprovalRoutes("activity_record")` would
 * have offered a risk the wrong routes, and the server refuses a route whose `applies_to` does not
 * match — a control that predictably 403s, which is the defect UX-04 names and
 * `30-design-principles.md` rule 6 forbids.
 *
 * **What a revision number means is the caller's business.** An activity record has no version
 * column — ADR-0030 makes effectiveness a chain and `invalidate_superseded` is what ends a pending
 * approval — while a document has a real `version` that `expected_version` checks. The caller knows
 * which it has; this module carries the number and does not interpret it.
 */

import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { useMe } from "../api/auth";
import { currentRoutes } from "../api/approvals";
import { useReader } from "../api/profile";
import { isAmbiguous } from "../api/refusal";
import {
  useAddNote,
  useApprovalRoutes,
  useDecideStage,
  useRaiseFlag,
  useRecordWork,
  useResolveFlag,
  useResubmitApproval,
  useSubmitForApproval,
  useWithdrawApproval,
} from "../api/work";
import type { Flag, Work } from "../api/work";
import { instantDate, shortDate } from "../lib/format";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import { mayDo } from "../shell/whoCan";
import { kindLabel } from "./kinds";
import { NewTask } from "./NewTask";
import { useNameOf } from "./PersonRef";
import { SignOffCard } from "./RecordSignOff";
import styles from "./Screen.module.css";
import own from "./Record.module.css";

/** Mirrored from `collaboration.py`'s `MAY_WRITE`. A Viewer and an Auditor read a record's work;
 * submitting it, flagging it and noting on it are the work.
 *
 * **Said rather than discovered** (design rule 6, UX-04). All three forms were rendered for every
 * role, so an Auditor could raise a flag, press the button and collect a 403 — on the surface
 * whose whole purpose is that an outside reader can examine what was done. */
const MAY_WRITE: readonly string[] = ["admin", "approver", "contributor"];

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** What an approver is attesting, said before the button (P15 item 4): the revision, the figure and
 * the factor edition it was priced with. Every part is read from the receipt by the caller; this
 * module prints it and checks nothing. Absent for a kind that has no figure. */
export interface Attest {
  revision: number;
  /** The figure as it is printed on the page, in tonnes. */
  figure: string;
  /** The figure's calculation, so the number in the statement opens its receipt (rule 3). */
  calculationId: string;
  /** Publisher and edition of the locked factor, as the receipt states them. */
  edition: string;
}

export function WorkflowTab({
  recordId,
  kind,
  revision,
  attest,
  onReceipt,
}: {
  recordId: string;
  kind: string;
  revision: number;
  attest?: Attest;
  /** Opens a figure's receipt. Passed by a page that has figures to open; without it the sign-off
   * card and the statement print no figure at all. */
  onReceipt?: (calculationId: string) => void;
}) {
  const read = useRecordWork(recordId);
  const { data: work, isPending } = read;
  const { data: me } = useMe();
  /** Re-read the record's work. Passed down rather than re-queried per panel: it is the remedy
   * for a stale refusal and for a request whose outcome is unknown, and four panels calling
   * `refetch` on four copies of the same hook is four things to keep in step. */
  const reload = () => void read.refetch();
  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not write".
  const mayWrite = me ? MAY_WRITE.includes(me.role) : undefined;

  if (isPending) {
    return (
      <Panel title="Workflow" column="1 / 13">
        <Empty>Loading…</Empty>
      </Panel>
    );
  }
  // **A failed read is not an unregistered record.** The sentence below asserts a specific gap in
  // the data — that nothing called `records.register` for this row — and a dropped connection
  // produced it, which sends a reader to look for a bug that is not there (EXP-29).
  if (read.error) {
    return (
      <Panel title="Workflow" column="1 / 13">
        <ReadFailed of="This record's workflow" error={read.error} onRetry={reload} />
      </Panel>
    );
  }
  if (!work) {
    return (
      <Panel title="Workflow" column="1 / 13">
        <p className={styles.note}>
          No work can be read for this record. It may not be registered as a referenceable record
          yet, which is a gap in the data rather than an empty workflow.
        </p>
      </Panel>
    );
  }

  return (
    <>
      {/* The permanent card for the last full approval: who, in what role, when, which entry, and
          whether it still holds. Nothing for a record nobody has fully approved. */}
      {work.standing && onReceipt ? (
        <div className={own.signoff}>
          <SignOffCard standing={work.standing} onReceipt={onReceipt} />
        </div>
      ) : null}
      <Approval
        recordId={recordId}
        kind={kind}
        revision={revision}
        work={work}
        me={me ?? null}
        reload={reload}
        attest={attest}
        onReceipt={onReceipt}
      />
      <Flags recordId={recordId} work={work} reload={reload} mayWrite={mayWrite} />
      <Notes
        recordId={recordId}
        revision={revision}
        work={work}
        reload={reload}
        mayWrite={mayWrite}
      />
      <Tasks work={work} recordId={recordId} />
    </>
  );
}

/** The request states in which somebody is still waiting — the client's half of `models.OPEN`.
 *
 * **`"pending"` was a synonym for "open" here, exactly as it was in five places on the server.**
 * ADR-0053 added `adjustment_requested`, and on the old check the reviewer who asked for a change
 * could never decide again: both branches were gated on `state === "pending"`, so the panel would
 * have shown an open request with no controls and no sentence — the worst of the two, because a
 * reader cannot tell a missing control from a finished request.
 *
 * A mirror of the backend's set rather than a second idea of what open means, and a `Set` rather
 * than `state !== "approved" && state !== …` so a sixth state has to be **classified** instead of
 * silently counting as open.
 */
const OPEN_STATES = new Set(["pending", "adjustment_requested"]);

/** Exported for the approvals screen's pane (F3): the decision is made by this panel wherever it is
 * drawn, so the server's refusals are rendered the same way in both places. */
export function Approval({
  recordId,
  kind,
  revision,
  work,
  me,
  reload,
  attest,
  onReceipt,
}: {
  recordId: string;
  kind: string;
  revision: number;
  work: Work;
  me: { user_id: string; role: string } | null;
  reload: () => void;
  attest?: Attest;
  onReceipt?: (calculationId: string) => void;
}) {
  //: Submitting a record for review is `MAY_WRITE` on the server. A Viewer shown a route picker
  //: and a submit button is shown a control that predictably 403s.
  const mayWrite = me ? MAY_WRITE.includes(me.role) : undefined;
  // **The route set is per kind**, and it was the string `"activity_record"`. A route declares
  // `applies_to`, so a risk offered an activity record's routes would offer a route the server
  // then refuses — a control that predictably 403s, which is the defect UX-04 names.
  const routes = useApprovalRoutes(kind);
  const submit = useSubmitForApproval(recordId);
  const decide = useDecideStage(recordId);
  const withdraw = useWithdrawApproval(recordId);
  const resubmit = useResubmitApproval(recordId);
  const personName = useNameOf();
  const [routeId, setRouteId] = useState("");
  const [rationale, setRationale] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const request = work.approval;
  const mineToWithdraw = Boolean(request && me && mayWrite && request.submitted_by === me.user_id
    && OPEN_STATES.has(request.state));

  //: **The submitter's answer to a request for a change** (ADR-0053 points 3 and 4, G-P5-03). Theirs
  //: alone and only while the reviewer is waiting on them: any other state has nothing to answer, and
  //: the reviewer who asked does not answer their own request.
  const mineToResubmit = Boolean(
    request && me && mayWrite && request.submitted_by === me.user_id && request.state === "adjustment_requested",
  );

  const stage = request?.stages.find((s) => s.position === request.current_position);
  // **The submitter of a request on a route that separates duties cannot decide any stage of it** (the
  // server refuses it, `approvals._require_separation`). Drawing Approve, Ask for a change and Reject
  // for them was a control certain to be refused, below the fold, naming no one to ask (Q-05,
  // G-Wapprover-04). The route is read from the routes this kind already fetched; when it cannot be
  // found the panel is drawn as before and the server stays the authority.
  const route = request ? routes.data?.find((candidate) => candidate.id === request.route_id) : undefined;
  const separated = Boolean(
    request && me && route?.requires_separation && request.submitted_by === me.user_id,
  );
  const namedForStage = Boolean(
    stage && me && stage.reviewer_id === me.user_id && mayDo(me.role, "decideApproval"),
  );
  const mineToDecide = namedForStage && !separated;

  async function onSubmit() {
    setRefusal(null);
    try {
      await submit.mutateAsync({ routeId, recordVersion: revision });
      setRouteId("");
    } catch (failure) {
      setRefusal(failure);
    }
  }

  async function onDecide(decision: "approve" | "reject" | "request_adjustment") {
    if (!request || request.current_position === null) return;
    setRefusal(null);
    try {
      await decide.mutateAsync({
        requestId: request.id,
        position: request.current_position,
        decision,
        rationale,
      });
      setRationale("");
    } catch (failure) {
      // **A stage decided twice, and a stage somebody else decided first, are both refused here**
      // — replay and stage order are two of S05's six server-side refusals. Which one it was is in
      // the code, and `WriteFailed` reads it: re-sending a decision against a request that has
      // moved on is exactly the retry that must not be offered.
      setRefusal(failure);
    }
  }

  //: A ref as well as `isPending`: two clicks inside one frame both read the render's `isPending`
  //: as false, and the second answer would be a refusal the person did nothing to deserve.
  const resubmitting = useRef(false);
  async function onResubmit() {
    if (!request || !mineToResubmit || resubmitting.current || decide.isPending || withdraw.isPending) return;
    resubmitting.current = true;
    setRefusal(null);
    try {
      await resubmit.mutateAsync(request.id);
    } catch (failure) {
      setRefusal(failure);
    } finally {
      resubmitting.current = false;
    }
  }

  async function onWithdraw() {
    if (!request || !mineToWithdraw || withdraw.isPending || withdraw.isError || decide.isPending) return;
    setRefusal(null);
    try {
      await withdraw.mutateAsync(request.id);
    } catch (failure) {
      setRefusal(failure);
    }
  }

  function reloadApproval() {
    setRefusal(null);
    withdraw.reset();
    reload();
  }

  return (
    <Panel
      title="Approvals"
      sub={request ? `${request.route_name} · ${request.state.replace(/_/g, " ")}` : "not in review"}
      column="1 / 7"
    >
      {!request || request.state === "withdrawn" ? (
        <>
          {request?.state === "withdrawn" ? <p className={styles.note} data-testid="approval-withdrawn">
            This request was withdrawn. Decisions and tasks remain. A fresh submission starts a new request.
          </p> : null}
          <p className={styles.note}>
            This record is not in review. Submitting it captures the route as it stands now and the
            revision you are looking at — editing a route afterwards publishes a new version and does
            not rewrite a request somebody has already acted on.
          </p>
          {/* **A failed read of the route set is not an absent one.** The sentence below says this
              workspace has configured no route for this kind — a claim about their setup — and a
              dropped request produced it. `routes.data` defaulting to `[]` inside the hook is what
              made the two indistinguishable here (EXP-29). */}
          {mayWrite === false ? (
            <p className={styles.note} data-testid="approval-who-can">
              An <strong>Admin</strong>, an <strong>Approver</strong> or a{" "}
              <strong>Contributor</strong> puts a record into review. Reading what is in review is
              how an engagement examines it; submitting one is preparing it.
            </p>
          ) : routes.error ? (
            <ReadFailed
              of="The review routes for this kind of record"
              error={routes.error}
              onRetry={() => void routes.refetch()}
            />
          ) : routes.data && routes.data.length > 0 ? (
            <>
              <label className={styles.field}>
                <span>Review route</span>
                <select
                  aria-label="Review route"
                  value={routeId}
                  onChange={(event) => setRouteId(event.target.value)}
                >
                  <option value="">Choose a route…</option>
                  {currentRoutes(routes.data).map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.name} · version {route.version} · {route.stages.length} stage
                      {route.stages.length === 1 ? "" : "s"}
                      {route.requires_separation ? " · separated" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <p className={cls(own.actions)}>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={!routeId || submit.isPending}
                  onClick={() => void onSubmit()}
                >
                  {submit.isPending ? "Sending…" : "Send for approval"}
                </button>
              </p>
            </>
          ) : (
            /* No route is a configuration gap, and it says so. A route with no stages would
               approve on submission — F07's shape exactly — so the server refuses one, and this
               says why nothing can be submitted rather than showing an empty dropdown. */
            <>
              <p className={styles.note} data-testid="no-route">
                No review route is configured for <strong>{kindLabel(kind)}</strong> in this
                workspace, so nothing can be submitted. A route needs at least one named reviewer:
                with none, a record would be approved as soon as it was submitted.
              </p>
              {/* **Who can fix it, and where** (journey walk, 2026-10-31). The sentence above said
                  what was wrong and left a Contributor, and an Admin, with nowhere to go: the
                  route is published in Admin, under Approval routes, by an Admin. */}
              {me?.role === "admin" ? (
                <p className={styles.note}>
                  <Link
                    className={styles.fixLink}
                    to="/admin"
                    search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "routes" })}
                    data-testid="no-route-publish"
                  >
                    Publish a review route
                  </Link>{" "}
                  in Admin, under Approval routes.
                </p>
              ) : (
                <p className={styles.note} data-testid="no-route-who-can">
                  An Admin publishes review routes, in Admin under Approval routes. Ask one to
                  publish a route for {kindLabel(kind)}.
                </p>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <ol className={cls(own.stages)}>
            {request.stages.map((s) => {
              const done =
                request.current_position === null || s.position < request.current_position;
              const here = s.position === request.current_position;
              return (
                <li
                  key={s.position}
                  className={cls(own.stage, here && own.stageHere, done && own.stageDone)}
                >
                  <span className={cls(own.stageName)}>
                    {s.position}. {s.name}
                  </span>
                  <span className={cls(own.stageWho)}>
                    {/* A reviewer is a UUID on the wire (rule 4). "You", or what the server says this
                        reader may be told (ADR-0218: a name, or for an Auditor the role). Never the
                        hex string, and the stage's own name above is the route author's wording. */}
                    {me && s.reviewer_id === me.user_id
                      ? "assigned to you"
                      : `assigned to ${personName(s.reviewer_id) ?? "a named reviewer"}`}
                    {here ? " · deciding now" : done ? " · decided" : " · waiting"}
                  </span>
                </li>
              );
            })}
          </ol>

          <p className={styles.note}>
            Submitted against revision {request.record_version}.{" "}
            {request.state === "invalidated"
              ? "The record changed after it was submitted, so this request takes no more decisions — it was superseded, not rejected, and nobody's judgement was overturned."
              : request.state === "approved"
                ? "Every stage has been decided. That is an approval of this revision, not a statement that a filing is lawfully fit to file."
                : request.state === "rejected"
                  ? "A reviewer rejected it. The rationale is on the decision."
                  : request.state === "adjustment_requested"
                    ? "A change was asked for. Once the submitter resubmits, the same reviewer decides again."
                    : "Stages are decided in order, by the named reviewer for each."}
          </p>

          {/* A blocking flag says so **here**, rather than only when somebody tries to approve and
              is refused. A warning flag never blocks, or the severity scale would be one value. */}
          {request.blocked_by.length > 0 ? (
            <p className={styles.error}>
              Held by {request.blocked_by.length} blocking flag
              {request.blocked_by.length === 1 ? "" : "s"}: {request.blocked_by.join("; ")}. Approval
              is unavailable until they are resolved; a rejection is not, because a reviewer who can
              see an unresolved problem should be able to say so.
            </p>
          ) : null}

          {mineToResubmit ? (
            <p className={own.actions}>
              <button
                type="button"
                className={styles.primary}
                data-testid="approval-resubmit"
                title="The same request goes back to the same reviewer. Withdrawing instead would restart it at stage 1."
                disabled={resubmit.isPending || withdraw.isPending || decide.isPending}
                onClick={() => void onResubmit()}
              >
                {resubmit.isPending ? "Sending…" : "Resubmit for review"}
              </button>
            </p>
          ) : null}

          {mineToWithdraw ? <div>
            <p className={styles.note}>Withdrawing ends your submission; decisions and tasks remain.</p>
            <p className={own.actions}>
              <button type="button" className={styles.secondary}
                disabled={withdraw.isPending || withdraw.isError || decide.isPending}
                onClick={() => void onWithdraw()}>
                {withdraw.isPending ? "Withdrawing…" : "Withdraw my submission"}
              </button>
            </p>
          </div> : null}

          {OPEN_STATES.has(request.state) && mineToDecide ? (
            <>
              <label className={styles.field}>
                <span>Why</span>
                <input
                  aria-label="Why"
                  value={rationale}
                  onChange={(event) => setRationale(event.target.value)}
                  placeholder="A decision with no reasoning cannot be reviewed later"
                />
              </label>
              {/* **What the signature means, said before the button.** An approval is of one revision
                  and the figure priced from it, with the factor edition it used; the card that
                  records it afterwards says the same three things. */}
              {attest && onReceipt ? (
                <p className={styles.note} data-testid="approval-attest">
                  Approving attests revision {attest.revision}:{" "}
                  <button
                    type="button"
                    className={styles.fixLink}
                    data-testid="approval-attest-receipt"
                    onClick={() => onReceipt(attest.calculationId)}
                  >
                    {attest.figure} tCO₂e
                  </button>
                  , priced with {attest.edition}.
                </p>
              ) : null}
              <p className={cls(own.actions)}>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={
                    !rationale.trim() || decide.isPending || withdraw.isPending || request.blocked_by.length > 0
                  }
                  onClick={() => void onDecide("approve")}
                >
                  Approve this stage
                </button>
                {/* **Between the two, because it is between them in meaning** (ADR-0053). A
                    reviewer who wanted a change used to have to press Reject, and a rejection is a
                    durable judgement an assurance provider reads as "this was refused". Not
                    disabled by a blocking flag, for the same reason a rejection is not: somebody
                    who can see an unresolved problem should be able to say so. */}
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={!rationale.trim() || decide.isPending || withdraw.isPending}
                  onClick={() => void onDecide("request_adjustment")}
                >
                  Ask for a change
                </button>
                <button
                  type="button"
                  className={styles.fixLink}
                  disabled={!rationale.trim() || decide.isPending || withdraw.isPending}
                  onClick={() => void onDecide("reject")}
                >
                  Reject
                </button>
              </p>
            </>
          ) : OPEN_STATES.has(request.state) && separated ? (
            /* Your own submission, on a route that separates duties: said before you press anything, with
               who can act. The stage may even be named to you; the route is why you cannot decide it. */
            <p className={styles.note} data-testid="approval-separated">
              You submitted this, and its route does not let the submitter decide. Withdraw it, then ask
              an Admin to publish a route that names someone else for stage {request.current_position}.
            </p>
          ) : OPEN_STATES.has(request.state) ? (
            /* Not your stage, said before you press anything. Having the right role is not the
               same as being the named reviewer, and a button that predictably 403s tells somebody
               they have a permissions problem they cannot fix. */
            <p className={styles.note}>
              Stage {request.current_position} is assigned to somebody else. Having the right role is
              not the same as being the named reviewer for a stage.
            </p>
          ) : null}
        </>
      )}

      <WriteFailed error={refusal} of="this approval" onReload={reloadApproval} />
      {withdraw.isError && !isAmbiguous(withdraw.error) ? <button type="button" className={styles.fixLink}
        onClick={reloadApproval}>Reload workflow</button> : null}
    </Panel>
  );
}

function Flags({
  recordId,
  work,
  reload,
  mayWrite,
}: {
  recordId: string;
  work: Work;
  reload: () => void;
  mayWrite: boolean | undefined;
}) {
  const raise = useRaiseFlag(recordId);
  const resolve = useResolveFlag(recordId);
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState<"information" | "warning" | "blocking">("warning");
  const [kind, setKind] = useState("");
  const [reason, setReason] = useState("");
  const [resolving, setResolving] = useState<string | null>(null);
  const [resolution, setResolution] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const blocking = work.flags.filter((f) => f.severity === "blocking" && f.state === "open");

  /** Close the raise form and leave nothing behind it. A cancel that clears the panel and keeps
   * the typed severity, kind and reason shows the next person opening it somebody else's
   * half-written flag — which is a draft nobody chose to keep rather than a convenience. */
  function cancelRaise() {
    setOpen(false);
    setSeverity("warning");
    setKind("");
    setReason("");
    setRefusal(null);
  }

  async function onRaise() {
    setRefusal(null);
    try {
      await raise.mutateAsync({ severity, kind: kind.trim(), reason: reason.trim() });
      setOpen(false);
      setKind("");
      setReason("");
    } catch (failure) {
      setRefusal(failure);
    }
  }

  async function onResolve(flag: Flag) {
    setRefusal(null);
    try {
      await resolve.mutateAsync({ flagId: flag.id, resolution: resolution.trim() });
      setResolving(null);
      setResolution("");
    } catch (failure) {
      setRefusal(failure);
    }
  }

  return (
    <Panel
      title="Flags"
      sub={
        work.flags.length
          ? `${work.flags.length} · ${blocking.length} blocking and open`
          : "none raised"
      }
      action={
        open || mayWrite === false ? undefined : (
          <button type="button" className={styles.fixLink} onClick={() => setOpen(true)}>
            Raise one
          </button>
        )
      }
      column="7 / 13"
    >
      {open ? (
        <div className={cls(own.form)}>
          <label className={styles.field}>
            <span>Severity</span>
            <select
              aria-label="Severity"
              value={severity}
              onChange={(event) =>
                setSeverity(event.target.value as "information" | "warning" | "blocking")
              }
            >
              <option value="information">Information — worth knowing</option>
              <option value="warning">Warning — does not hold an approval</option>
              <option value="blocking">Blocking — holds the approval until resolved</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>What kind</span>
            <input
              aria-label="What kind"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              placeholder="e.g. meter mismatch"
            />
          </label>
          <label className={styles.field}>
            <span>Why</span>
            <input
              aria-label="Why this is flagged"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="A flag with no reason cannot be resolved by anyone else"
            />
          </label>
          <p className={cls(own.actions)}>
            <button
              type="button"
              className={styles.primary}
              disabled={!kind.trim() || !reason.trim() || raise.isPending}
              onClick={() => void onRaise()}
            >
              {raise.isPending ? "Raising…" : "Raise it"}
            </button>
            <button type="button" className={styles.fixLink} onClick={cancelRaise}>
              Cancel
            </button>
          </p>
        </div>
      ) : null}

      {mayWrite === false ? (
        <p className={styles.note} data-testid="flags-who-can">
          An <strong>Admin</strong>, an <strong>Approver</strong> or a{" "}
          <strong>Contributor</strong> raises and resolves a flag. A flag holds an approval, so
          raising one is an assertion about the record rather than a remark on it.
        </p>
      ) : null}

      {work.flags.length === 0 ? (
        <Empty>Nothing is flagged on this record.</Empty>
      ) : (
        <ul className={cls(own.list)}>
          {work.flags.map((flag) => (
            <li key={flag.id} className={cls(own.item)}>
              <span className={cls(own.itemMain)}>
                <span className={cls(own.itemTitle)}>
                  {flag.kind}
                  <span
                    className={cls(
                      own.sev,
                      flag.severity === "blocking" && own.sevBlocking,
                      flag.severity === "warning" && own.sevWarning,
                    )}
                  >
                    {flag.severity}
                  </span>
                  {flag.state === "resolved" ? (
                    <span className={cls(own.sev, own.sevDone)}>resolved</span>
                  ) : null}
                </span>
                <span className={cls(own.itemBody)}>{flag.reason}</span>
                {flag.resolution ? (
                  <span className={cls(own.itemBody)}>Resolved: {flag.resolution}</span>
                ) : null}
              </span>
              {flag.state === "open" && mayWrite !== false ? (
                resolving === flag.id ? (
                  <span className={cls(own.form)}>
                    <label className={styles.field}>
                      <span>How it was resolved</span>
                      <input
                        aria-label="How it was resolved"
                        value={resolution}
                        onChange={(event) => setResolution(event.target.value)}
                        placeholder="Resolving without saying how is how a blocking flag becomes a formality"
                      />
                    </label>
                    <span className={cls(own.actions)}>
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={!resolution.trim() || resolve.isPending}
                        onClick={() => void onResolve(flag)}
                      >
                        Resolve
                      </button>
                      <button
                        type="button"
                        className={styles.fixLink}
                        onClick={() => {
                          setResolving(null);
                          setResolution("");
                          setRefusal(null);
                        }}
                      >
                        Cancel
                      </button>
                    </span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.fixLink}
                    onClick={() => {
                      setResolving(flag.id);
                      setResolution("");
                    }}
                  >
                    Resolve
                  </button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <WriteFailed error={refusal} of="this record's flags" onReload={reload} />
    </Panel>
  );
}

function Notes({
  recordId,
  revision,
  work,
  reload,
  mayWrite,
}: {
  recordId: string;
  revision: number;
  work: Work;
  reload: () => void;
  mayWrite: boolean | undefined;
}) {
  const reader = useReader();
  const add = useAddNote(recordId);
  const [body, setBody] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  async function onAdd() {
    setRefusal(null);
    try {
      await add.mutateAsync({ body: body.trim(), recordVersion: revision });
      setBody("");
    } catch (failure) {
      setRefusal(failure);
    }
  }

  return (
    <Panel
      title="Notes"
      sub={work.notes.length ? `${work.notes.length} on this record` : "none yet"}
      column="1 / 7"
    >
      {/* **A note is not a flag**, and this panel does not pretend otherwise. A note is a remark; a
          flag is an assertion that something is wrong, and only the second can hold an approval. */}
      {work.notes.length === 0 ? (
        <Empty>
          No notes. A note records a remark about this record — it does not hold an approval, which is
          what a flag is for.
        </Empty>
      ) : (
        <ul className={cls(own.list)}>
          {work.notes.map((note) => (
            <li key={note.id} className={cls(own.item)}>
              <span className={cls(own.itemMain)}>
                <span className={cls(own.itemBody)}>{note.body}</span>
                <span className={cls(own.itemMeta)}>
                  {instantDate(note.created_at, reader)}
                  {note.record_version !== null
                    ? ` · about revision ${note.record_version}`
                    : " · revision not recorded"}
                  {note.became_task_id ? " · became a task" : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {mayWrite === false ? (
        <p className={styles.note} data-testid="notes-who-can">
          An <strong>Admin</strong>, an <strong>Approver</strong> or a{" "}
          <strong>Contributor</strong> adds a note. Reading the thread is how an engagement follows
          what was decided.
        </p>
      ) : (
        <>
      <label className={styles.field}>
        <span>Add a note</span>
        <input
          aria-label="Add a note"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="What the next reader needs to know"
        />
      </label>
      <p className={cls(own.actions)}>
        <button
          type="button"
          className={styles.primary}
          disabled={!body.trim() || add.isPending}
          onClick={() => void onAdd()}
        >
          {add.isPending ? "Saving…" : `Add it, about revision ${revision}`}
        </button>
        {/* A discard rather than a cancel, because this field is always on screen — and it appears
            only once there is a draft to discard, so it is never a control that does nothing. */}
        {body ? (
          <button
            type="button"
            className={styles.fixLink}
            onClick={() => {
              setBody("");
              setRefusal(null);
            }}
          >
            Discard this note
          </button>
        ) : null}
      </p>
        </>
      )}
      <WriteFailed error={refusal} of="this record's notes" onReload={reload} />
    </Panel>
  );
}

function Tasks({ work, recordId }: { work: Work; recordId: string }) {
  return (
    <Panel
      title="Tasks"
      sub={work.tasks.length ? `${work.tasks.length} on this record` : "none"}
      column="7 / 13"
    >
      {work.tasks.length === 0 ? (
        <Empty>No tasks on this record.</Empty>
      ) : (
        <ul className={cls(own.list)}>
          {work.tasks.map((task) => (
            <li key={task.id} className={cls(own.item)}>
              <span className={cls(own.itemMain)}>
                <span className={cls(own.itemTitle)}>{task.title}</span>
                <span className={cls(own.itemMeta)}>
                  {task.state.replace(/_/g, " ")}
                  {task.due_date ? ` · due ${shortDate(task.due_date)}` : " · no due date"}
                  {task.priority === "high" ? " · high" : ""}
                </span>
                {task.completion_criteria ? (
                  <span className={cls(own.itemBody)}>{task.completion_criteria}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className={cls(own.actions)}>
        {/* Closing a task needs a completion note, and that form is on the queue. One place that
            closes a task rather than two that have to agree about what closing means. */}
        <NewTask recordId={recordId} />
        <Link className={styles.fixLink} to="/tasks" search={keepSearch}>
          Open the work queue ›
        </Link>
      </p>
    </Panel>
  );
}
